import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "../lib/api";

const AuthContext = createContext(null);

export const MODULES = [
  "dashboard",
  "calendar",
  "enquiries",
  "patients",
  "appointments",
  "reports",
  "treatments",
  "invoices",
  "reminders",
  "templates",
  "doctors",
  "settings",
  "staff_roles",
];

const DEFAULT_STAFF_MODULES = [
  "dashboard",
  "calendar",
  "enquiries",
  "patients",
  "appointments",
  "reports",
  "treatments",
  "invoices",
  "reminders",
];

const DEFAULT_DOCTOR_MODULES = [
  "dashboard",
  "calendar",
  "enquiries",
  "patients",
  "appointments",
  "reports",
  "treatments",
  "invoices",
  "reminders",
];

export function getUserModules(user) {
  if (!user) return [];
  if (user.role === "admin") return MODULES;
  if (Array.isArray(user.modules) && user.modules.length > 0) return user.modules;
  if (user.role === "doctor") return DEFAULT_DOCTOR_MODULES;
  if (user.role === "staff") return DEFAULT_STAFF_MODULES;
  return [];
}

export function hasModuleAccess(user, module) {
  return getUserModules(user).includes(module);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = unknown/checking, false = unauthenticated, object = authenticated
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      return { ok: true, user: data };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const register = async (payload) => {
    try {
      const { data } = await api.post("/auth/register", payload);
      setUser(data);
      return { ok: true, user: data };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
